import classNames from 'classnames';
import { ShareButtonSecondary } from 'components/common-form/sidePanel/docToolbar/summary/ShareButtonSecondary';
import { moreAgressiveColorMapper } from 'components/PostTrade/ProductModification/components/ValidationErrors.utils';
import { OrderPlacedDetails } from 'components/secondaryMarket/cpOrder/sidePanel/orderPlaced/OrderPlacedDetails';
import { ConnectedOrderPlacedActionButtons } from 'components/secondaryMarket/shared/SidePanel/OrderPlacedActionButtons';
import { TradeWay } from 'components/secondaryMarket/shared/SidePanel/TradeWay';
import { MessagesPanel } from 'components/secondaryMarket/shared/status';
import { useErrorMessagesFormatter } from 'components/secondaryMarket/shared/status/messages/useErrorMessageFormatter';
import { PanelFooter } from 'components/secondaryMarket/shared/status/Panel';
import { useEffectOnce } from 'hooks/useEffectOnce';
import { useUserNotification } from 'hooks/useUserNotification';
import { FC } from 'react';
import type { HighlightedContent } from 'state/notifications/notification.model';

import { OrderPlacedHeader } from '../../../shared/SidePanel/OrderPlacedHeader';
import { useFormattedOrderPlaced } from '../../../shared/SidePanel/useFormattedOrderPlaced';
import type { OrderPlacedSidePanelProps } from './OrderPlacedSidePanel.connect';

export const OrderPlacedSidePanel: FC<OrderPlacedSidePanelProps> = ({
  tabId,
  onDone,
  hasOrderBlotter,
  orderId,
  className,
  closeTabWhenLiveTradeCpOrderIsPlacedActivated,
  ...details
}) => {
  const { summary, hint, formatMessage } = useFormattedOrderPlaced(details);
  const { info, warn } = useUserNotification();
  const messages = useErrorMessagesFormatter(details.validationErrors, moreAgressiveColorMapper);
  useEffectOnce(() => {
    if (!closeTabWhenLiveTradeCpOrderIsPlacedActivated) {
      return;
    }
    const title = formatMessage({ id: 'secondaryMarket.sidePanel.label.orderPlaced' });
    if (details.validationErrors.length > 0) {
      warn({
        title: { content: title, severity: 'warning' },
        content: [
          { content: summary, severity: 'info' },
          ...messages.map(m => ({ isHtml: true, content: m.htmlMessage, severity: 'warning' }) as HighlightedContent),
        ],
      });
    } else {
      info({
        title: { content: title, severity: 'info' },
        content: [{ content: summary, severity: 'info' }],
      });
    }
    onDone(true);
  });

  return (
    <div className={classNames('d-flex flex-column', className)}>
      <OrderPlacedHeader summary={summary} hint={hint}>
        <div className="d-flex justify-content-between">
          <TradeWay tradeWay={details.tradeWay} />
          <ShareButtonSecondary
            tabId={tabId}
            color="tradable"
            mustForceAdvancedMode={false}
            workspaceType="secondaryMarketQuotes"
            type="cp"
            summary={summary}
          />
        </div>
      </OrderPlacedHeader>
      <PanelFooter color="info" statusKey="secondaryMarket.sidePanel.label.orderPlaced" icon="check_circle" />
      <MessagesPanel messagesToDisplay={messages} className="pt-2" e2e="side-panel-header" />
      <div className="flex-grow-1">
        <OrderPlacedDetails orderId={orderId} {...details} />
      </div>
      <ConnectedOrderPlacedActionButtons
        hasOrderBlotter={hasOrderBlotter}
        onDone={onDone}
        orderId={orderId}
        blotterRoute="/secondary-orders-blotter"
      />
    </div>
  );
};
OrderPlacedSidePanel.displayName = 'OrderPlacedSidePanel';
